import { useState } from 'react';
import { ViewTab, ViewTabList } from '@wundo/ui';
import {
  renderMeetingReport,
  type MeetingPageResponse,
  type MeetingPrepContent,
  type MeetingReportPending,
  type MeetingReportSection,
} from '@wundo/shared/meetingPage.ts';
import {
  deleteMeetingMaterial,
  fetchMeetingNote,
  type DocumentSaveOptions,
} from '@/lib/meetingPage.ts';
import type { RichTextEditorWiring } from '@/lib/richTextEditor.ts';
import { meetingActionItemHandlers } from './meetingActionItemHandlers.ts';
import { MeetingPrepPanel } from './MeetingPrepPanel.tsx';
import { MeetingReportPanel, type MeetingReportWaiting } from './MeetingReportPanel.tsx';
import type { MeetingPanel } from './meetingPanel.ts';
import type { MeetingWrites } from './useMeetingWrites.ts';
import { m } from '@/paraglide/messages.js';

/* How long "Gekopieerd" stays on the button before it goes back to normal. */
const COPIED_FOR_MS = 2000;

export interface MeetingDocumentsProps {
  page: MeetingPageResponse;
  userId: string;
  meetingId: string;
  panel: MeetingPanel;
  onPanelChange: (panel: MeetingPanel) => void;
  editor: RichTextEditorWiring;
  generation: Pick<MeetingWrites['generation'], 'isPending' | 'isError' | 'mutate'>;
  pasteGenerate: Pick<MeetingWrites['pasteGenerate'], 'isPending' | 'isError' | 'mutate'>;
  /* A stop the server has not heard yet: the verslag tab already waits for it. */
  microphoneStopping: boolean;
  /* The user's own recording could not be read back. */
  recordingFailed: boolean;
  /* The page's correction, for the agreements and the material. */
  run: (task: () => Promise<void>) => void;
  onGeneratePrep: () => void;
  onSavePrep: (content: MeetingPrepContent, options: DocumentSaveOptions) => void;
  onSaveReport: (sections: MeetingReportSection[], options: DocumentSaveOptions) => void;
  onRegenerate: (panel: MeetingPanel) => void;
}

/**
 * The meeting's two documents, the preparation and the verslag, one at a time.
 * Pills rather than underlines: these switch between two views of one meeting,
 * where section tabs elsewhere switch between parts of a page.
 */
export function MeetingDocuments({
  page,
  userId,
  meetingId,
  panel,
  onPanelChange,
  editor,
  generation,
  pasteGenerate,
  microphoneStopping,
  recordingFailed,
  run,
  onGeneratePrep,
  onSavePrep,
  onSaveReport,
  onRegenerate,
}: MeetingDocumentsProps) {
  const [copied, setCopied] = useState(false);
  const actionItems = meetingActionItemHandlers(userId, meetingId, run);
  /* The server's answer, plus what only this page knows: a write it asked, a stop. */
  const reportWaiting = reportWaitingOn(
    generation.isPending || pasteGenerate.isPending,
    microphoneStopping,
    page.report_pending,
  );

  /* The clipboard is the browser's; each document hands over its own text. */
  const copy = (text: string) => {
    void navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), COPIED_FOR_MS);
  };

  /* The page carries an excerpt; the full note is fetched when the dialog opens. */
  const loadNote = async () => {
    const note = page.materials.find((material) => material.kind === 'pasted');
    return note ? fetchMeetingNote(userId, meetingId, note.id) : null;
  };

  return (
    <div className='mt-4.5'>
      <div className='min-w-0'>
        <ViewTabList>
          <ViewTab active={panel === 'prep'} onClick={() => onPanelChange('prep')}>
            {m.meeting_tab_prep()}
          </ViewTab>
          <ViewTab active={panel === 'report'} onClick={() => onPanelChange('report')}>
            {m.meeting_tab_report()}
          </ViewTab>
        </ViewTabList>
        {panel === 'prep' ? (
          <MeetingPrepPanel
            page={page}
            generating={generation.isPending}
            generateFailed={generation.isError}
            copied={copied}
            editor={editor}
            actionItems={actionItems}
            onGenerate={onGeneratePrep}
            onRegenerate={() => onRegenerate('prep')}
            onSave={onSavePrep}
            onCopy={() => copy(page.prep?.content?.text ?? '')}
          />
        ) : (
          <MeetingReportPanel
            page={page}
            waiting={reportWaiting}
            recordingFailed={recordingFailed}
            generating={reportWaiting === 'writing'}
            generateFailed={generation.isError || pasteGenerate.isError}
            copied={copied}
            editor={editor}
            actionItems={actionItems}
            onGenerate={() => generation.mutate({ panel: 'report', regenerate: false })}
            onPasteGenerate={pasteGenerate.mutate}
            onLoadNote={loadNote}
            onRegenerate={() => onRegenerate('report')}
            onSave={onSaveReport}
            onDeleteMaterial={(materialId) =>
              run(() => deleteMeetingMaterial(userId, meetingId, materialId))
            }
            onCopy={() => copy(renderMeetingReport(page.report?.sections ?? []))}
          />
        )}
      </div>
    </div>
  );
}

function reportWaitingOn(
  writeAsked: boolean,
  microphoneStopping: boolean,
  pending: MeetingReportPending | null,
): MeetingReportWaiting | null {
  if (writeAsked || pending === 'debrief') return 'writing';
  if (microphoneStopping || pending === 'microphone') return 'microphone';
  if (pending === 'notetaker') return 'notetaker';
  return null;
}
